import React, { useEffect, useRef } from 'react';

import { Box } from '@rbilabs/universal-components';

import useMap from 'hooks/use-map';
import { CenterPoint, MarkerTypes } from 'hooks/use-map/types';
import { useGeolocation } from 'state/geolocation';

import { IMarker, IStoreLocationMap } from './types';

export const StoreLocationMap: React.FC<React.PropsWithChildren<IStoreLocationMap>> = ({
  storeLocation,
  markers = [],
  title,
  showUserMarker = false,
  isStaticMap = false,
}) => {
  const timerRef = useRef<NodeJS.Timeout>(undefined);
  const { isPermissionGranted, userCoordinates } = useGeolocation();

  const handleDragEnd = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const { map, createMarker, fitAndCenterFromCoords, clearMarkers } = useMap({
    position: storeLocation as CenterPoint,
    isStaticMap,
    eventListeners: {
      dragend: handleDragEnd,
    },
  });

  useEffect(() => {
    clearMarkers();

    const storeMarker: IMarker = {
      id: 'store',
      type: MarkerTypes.StoreOpenSelected,
      location: storeLocation,
      title,
    };

    if (isPermissionGranted && userCoordinates && showUserMarker) {
      const userMarker: IMarker = {
        id: 'user',
        type: MarkerTypes.User,
        location: userCoordinates,
        title,
      };

      markers.push(userMarker);
    }

    const allMarkers = [storeMarker, ...markers];
    const allCoordinates = allMarkers.map(marker => marker.location);

    allMarkers.forEach(marker => {
      createMarker({
        ...marker,
      });
    });

    if (allCoordinates.length > 1) {
      fitAndCenterFromCoords(allCoordinates as CenterPoint[]);
    }
  }, [
    isPermissionGranted,
    userCoordinates,
    showUserMarker,
    markers,
    storeLocation,
    title,
    createMarker,
    fitAndCenterFromCoords,
    clearMarkers,
  ]);

  useEffect(() => {
    const timer = timerRef.current;
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  return (
    <Box flex="1" pointerEvents={isStaticMap ? 'none' : 'auto'}>
      {map}
    </Box>
  );
};
